import React, { FC, useCallback, useState, useEffect } from 'react';

import {
  Link,
  Alert,
  AlertDescription,
  Button,
  CloseButton,
  Text,
  Box,
  Input,
  ModalCloseButton,
  FormControl,
  FormLabel,
} from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

import { LoginJoinModal } from '../form/LoginJoinModal';

interface Props {
  xsrft: string;
  closeModal: () => void;
  onSuccessfulLogin: (response: { signup: boolean }) => void;
  submitCommentAsGuest: (guestName: string) => void;
  isSubmitLoading: boolean;
  apiErrorCode?: number | null;
  resetApiErrorCode?: (errorCode: number) => void | undefined;
  titleText?: string;
  namePlaceholderText?: string;
  isCompact?: boolean;
  onLoginModalOpenChange?: (isOpen: boolean) => void;
}

// set the redirect URL for social media SSO logins.
// .substring(1) removes the appended /, which accommodates
// the behavior of the backend router
const redirectUrl =
  typeof window !== 'undefined' ? window.location.pathname.substring(1) : undefined;

const ReviewAsGuestForm: FC<Props> = ({
  xsrft,
  closeModal,
  onSuccessfulLogin,
  submitCommentAsGuest,
  isSubmitLoading,
  apiErrorCode,
  resetApiErrorCode,
  titleText,
  namePlaceholderText,
  isCompact = false,
  onLoginModalOpenChange,
}) => {
  const [nameValue, updateNameValue] = useState<string>('');
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
  const EMAIL_ALREADY_EXIST_ERROR = 2437;
  const BAD_ARGUMENTS_PROVIDED = 2204;
  const TOO_MANY_REQUESTS = 10022;

  useEffect(() => {
    const isInvalidName = !nameValue.trim();
    setDisableSubmit(isInvalidName);
  }, [nameValue]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onCommentAsGuest();
    }
  };

  const resetErrors = () => {
    resetApiErrorCode?.(0);
    updateNameValue('');
  };

  const handleClose = () => {
    resetErrors();
    closeModal();
  };

  const onCommentAsGuest = () => {
    if (nameValue.trim()) {
      resetApiErrorCode?.(0);
      submitCommentAsGuest(nameValue);
    }
  };

  const completeLogin = useCallback((response: { signup: boolean }) => {
    onSuccessfulLogin(response);
  }, []);

  return (
    <Box
      borderRadius="xl"
      display="flex"
      flexDirection="column"
      alignItems="center"
      padding={isCompact ? '24px' : '48px'}
      position={isCompact ? 'relative' : undefined}
    >
      {isCompact ? (
        <CloseButton
          variant="tertiary"
          size="xs"
          position="absolute"
          top="16px"
          right="16px"
          aria-label="close button"
          onClick={closeModal}
        />
      ) : (
        <ModalCloseButton size="lg" aria-label="close button" onClick={handleClose} />
      )}
      <Box width={isCompact ? '100%' : ['310px', '322px']}>
        <Text variant={isCompact ? 'heading-sm' : 'heading-md'} sx={{ mb: isCompact ? '4' : '2' }}>
          {titleText ?? translate('Let others know who you are')}
        </Text>
        <FormControl mb="4">
          <FormLabel mb="2" fontFamily="heading" fontSize={isCompact ? '12px' : '14px'}>
            {translate('Name')}
          </FormLabel>
          <Input
            id="name"
            size="md"
            placeholder={namePlaceholderText ?? translate('First and last name')}
            value={nameValue}
            type="text"
            _placeholder={{ color: 'text-secondary' }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              updateNameValue(event.target.value);
            }}
            onKeyDown={handleKeyDown}
          />
        </FormControl>

        {[TOO_MANY_REQUESTS, BAD_ARGUMENTS_PROVIDED, EMAIL_ALREADY_EXIST_ERROR].includes(
          apiErrorCode ?? 0,
        ) && (
          <Alert status="error" my="4">
            <AlertDescription fontSize="body-sm" pt="1">
              {translate('Unable to process your request. Try again later.')}
            </AlertDescription>
          </Alert>
        )}

        <Button
          variant="primary"
          padding="5px 0px"
          width="100%"
          onClick={onCommentAsGuest}
          isDisabled={disableSubmit}
          isLoading={isSubmitLoading}
        >
          {translate('Post comment')}
        </Button>
      </Box>

      <LoginJoinModal
        type="login"
        xsrft={xsrft}
        onSuccess={(response) => {
          onLoginModalOpenChange?.(false);
          completeLogin(response);
        }}
        onDismiss={() => onLoginModalOpenChange?.(false)}
        redirectUrl={redirectUrl}
      >
        {(toggleModal) => (
          <Text variant={isCompact ? 'body-sm' : 'body-lg'} textAlign="center" mt={16}>
            {translate({
              singular: 'Have a Vimeo account? {LINK}Log in{/LINK}',
              replacements: {
                LINK: (text: string) => (
                  <Link
                    cursor="pointer"
                    tabIndex={0}
                    variant="inline-primary"
                    textUnderlineOffset="2px"
                    onClick={() => {
                      resetErrors();
                      onLoginModalOpenChange?.(true);
                      toggleModal();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        resetErrors();
                        onLoginModalOpenChange?.(true);
                        toggleModal();
                      }
                    }}
                  >
                    {text}
                  </Link>
                ),
              },
            })}
          </Text>
        )}
      </LoginJoinModal>
      <Text color="text-tertiary" variant={isCompact ? 'body-sm' : 'heading-xs'} mt="4">
        {translate({
          singular:
            'By posting a comment, you agree to our {TERMS_OF_SERVICE} Terms of Service {/TERMS_OF_SERVICE} and acknowledge our {PRIVACY_POLICY} Privacy Policy {/PRIVACY_POLICY}',
          replacements: {
            TERMS_OF_SERVICE: (text: string) => <PagesLink text={text} href={'/terms'} />,
            PRIVACY_POLICY: (text: string) => <PagesLink text={text} href={'/privacy'} />,
          },
        })}
      </Text>
    </Box>
  );
};

const PagesLink = ({ text, href }: { text: string; href: string }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    color="text-teritary"
    variant="inline-secondary"
    fontSize="inherit"
    key={href}
  >
    {text}
  </Link>
);
export default ReviewAsGuestForm;
